import React from 'react';
import { Helmet } from 'react-helmet-async';

export const DefaultSEO = ({
  title = 'Your Event Venue Concierge',
  description = 'We find the venue, you take the credit.',
  canonical = 'https://letshost.nyc',
  ogImage = '/letshost-share-preview.png',
}) => {
  // Always use an absolute URL for social images
  const absoluteImageUrl = ogImage.startsWith('http') 
    ? ogImage 
    : `https://letshost.nyc${ogImage.startsWith('/') ? '' : '/'}${ogImage}`;

  // Create a structured object for meta tags
  const metaTags = [
    // Critical OG image tags (placed first for priority)
    { property: "og:image", content: absoluteImageUrl },
    { property: "og:image:url", content: absoluteImageUrl },
    { property: "og:image:secure_url", content: absoluteImageUrl },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:type", content: "image/png" },
    
    // Other Open Graph
    { property: "og:type", content: "website" },
    { property: "og:url", content: canonical },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:site_name", content: "LetsHost NYC" },
    
    // Twitter
    { name: "twitter:card", content: "summary_large_image" },
    { name: "twitter:url", content: canonical },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: absoluteImageUrl },
    
    // Standard
    { name: "description", content: description },
    { name: "keywords", content: "NYC restaurant event space, bar event space NYC, corporate event venues NYC, birthday party venues NYC, happy hour spaces NYC, large group reservations NYC, event venue booking NYC, NYC birthday venues, speakeasy birthday NYC, rooftop birthday party, West Village birthday venues, Chelsea happy hour, Midtown corporate events, Financial District event spaces, Lower East Side party venues, NYC venue concierge, Manhattan event planning" },
    
    // Additional tags
    { name: "robots", content: "index, follow" },
    { name: "googlebot", content: "index, follow" },
    { name: "format-detection", content: "telephone=no" },
    
    // iOS
    { name: "apple-mobile-web-app-capable", content: "yes" },
    { name: "apple-mobile-web-app-status-bar-style", content: "black" },
    { name: "apple-mobile-web-app-title", content: title },
    
    // Location
    { name: "geo.region", content: "US-NY" },
    { name: "geo.placename", content: "New York City" },
    { name: "geo.position", content: "40.7128;-74.0060" },
    { name: "ICBM", content: "40.7128, -74.0060" }
  ];

  return (
    <Helmet
      defaultTitle={title}
      titleTemplate="%s"
      meta={metaTags}
    >
      {/* Rich Snippets - Local Business */}
      <script type="application/ld+json">
        {`
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "LetsHost NYC",
            "description": "Find and book NYC restaurant and bar spaces for your next event.",
            "image": "${absoluteImageUrl}",
            "url": "${canonical}",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "New York",
              "addressRegion": "NY",
              "addressCountry": "US"
            },
            "areaServed": {
              "@type": "City",
              "name": "New York City"
            },
            "priceRange": "$$-$$$$",
            "makesOffer": [
              {
                "@type": "Offer",
                "name": "Restaurant Event Booking",
                "description": "Book NYC restaurants for events and group dining"
              },
              {
                "@type": "Offer",
                "name": "Bar Space Reservations",
                "description": "Reserve bar areas for happy hours and celebrations"
              },
              {
                "@type": "Offer",
                "name": "Large Group Events",
                "description": "Book venues for corporate and social gatherings"
              },
              {
                "@type": "Offer",
                "name": "Birthday Party Venues",
                "description": "Find the perfect space for birthday celebrations"
              },
              {
                "@type": "Offer",
                "name": "NYC Event Planning Guides",
                "description": "Expert guides for planning events in NYC with venue recommendations"
              }
            ]
          }
        `}
      </script>
      
      {/* Favicon */}
      <link rel="icon" href="/favicon.ico" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon/favicon-32x32.png" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon/favicon-16x16.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/favicon/apple-touch-icon.png" />
      <link rel="manifest" href="/site.webmanifest" />
      <meta name="theme-color" content="#ffffff" />
    </Helmet>
  );
}; 